import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

import { forgotPassword, getProfile, verifyTokenUpdatePassword, signinAPI } from 'src/apis/Authenticates/signin';
import { TProfile } from 'src/types/profiles/profile.model';
import { useAppStore } from 'src/context/AppStore';
import { TForgotPassword, TSignin, TSignInResponse } from 'src/types/Authens/signin.model';
import api from 'src/configs/api';
import { getAppInfo } from 'src/apis/Commons/commons';
import useLanguage from '../Language/useLanguage';
import { PathURL } from 'src/utils/constants/path';
import Swal from 'sweetalert2';
import { TBaseResponse } from 'src/types/Commons/filter.model';

function useAuthen() {
  const { language } = useLanguage();
  const appStore = useAppStore();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const defaultEmail: string = appStore.currentEmail || '';

  const mutation = useMutation((item: TSignin) => signinAPI(item), {
    onSuccess: async (response: TBaseResponse<TSignInResponse>) => {
      if (!response.succeeded) {
        setErrorMessage(response.error?.message || language.signInPage.validation.inCorrect);
        return;
      }

      const { accessToken, expiresIn, refreshToken } = response.data;

      if (accessToken) {
        appStore.updateUserToken(accessToken, refreshToken, expiresIn.toString());
        api.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

        await mutationGetProfile.mutateAsync();
        await mutationGetAppInfo.mutateAsync();
      } else {
        appStore.clearPersistedData();
      }
    },
    onError: (errors: any) => {
      if (errors?.response) {
        const {
          data: { error_description }
        } = errors.response;
        if (error_description) {
          error_description.includes('AADB2C90225')
            ? setErrorMessage(language.signInPage.validation.inCorrect)
            : setErrorMessage(error_description);
        }
      } else {
        setErrorMessage('Something went wrong! server unavailable');
      }
      appStore.clearPersistedData();
    }
  });

  const mutationGetProfile = useMutation(() => getProfile(), {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSuccess: async (response: any) => {
      const newInfo: TProfile = { ...response.data };

      newInfo.userId = response.data.id;
      newInfo.companyName = response.data.company;
      newInfo.teamMembers = response.data.teamMembersCount;
      newInfo.coverImg = '';

      appStore.updateProfile(newInfo);
    }
  });

  const mutationForgotPassword = useMutation((email: string) => forgotPassword(email), {
    onSuccess: async (response: any) => {
      if (response.succeeded && response.data) {
        Swal.fire({
          icon: 'success',
          text: language.profilePage.validation.resetPassword.checkEmail
        });
        return;
      } else {
        const { error } = response;
        Swal.fire({
          icon: 'error',
          text: error.message
        });
        return;
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      if (error.response) {
        const { data } = error.response;
        if (data) {
          setErrorMessage(data.message);
        }
      } else {
        setErrorMessage(error.message);
      }
    }
  });

  const mutationGetAppInfo = useMutation(() => getAppInfo(), {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSuccess: async (response: any) => {
      appStore.updateAppInfo(response);
    }
  });

  const mutationVerifyTokenUpdatePassword = useMutation((param: TForgotPassword) => verifyTokenUpdatePassword(param), {
    onSuccess: async (response: any) => {
      const isSuccess = response.succeeded && response.data;
      if (!isSuccess) {
        const { error } = response;
        Swal.fire({
          icon: 'error',
          title: error.message,
          showConfirmButton: false,
          timer: 2500
        });
        return;
      }
      Swal.fire({
        icon: 'success',
        title: 'Successfully',
        showConfirmButton: false,
        timer: 2500
      }).then(function () {
        navigate(`${PathURL.signin}`);
      });
    }
  });

  async function login(data: TSignin) {
    setErrorMessage(null);
    appStore.clearPersistedData();
    await mutation.mutateAsync(data);
  }

  async function signOut(event: any) {
    event.preventDefault();
    logOutClearData();
  }

  async function logOutClearData() {
    await appStore.clearPersistedData();
    navigate(`${PathURL.signin}`);
  }

  return {
    login,
    signOut,
    logOutClearData,
    mutation,
    errorMessage,
    mutationForgotPassword,
    mutationVerifyTokenUpdatePassword,
    mutationGetProfile,
    defaultEmail
  };
}

export default useAuthen;
